import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Loader2, LogIn, LogOut, Eye, Users, FileText, Sparkles } from "lucide-react";
import { listCaseStudies, adminVerify, adminCreateCase, adminUpdateCase, adminDeleteCase, listTeam, adminCreateTeam, adminUpdateTeam, adminDeleteTeam, adminTeamRemoveBg, adminTeamRemoveBgAll, adminTeamRemoveBgStatus } from "@/lib/api";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const TOKEN_KEY = "lovac_admin_token";

const EMPTY_CASE = {
  slug: "",
  nome_azienda: "",
  settore: "",
  tipo_figura: "Sales",
  ruolo_cercato: "",
  time_to_hire: "",
  titolo_pagina: "",
  citazione_principale: "",
  nome_cliente: "",
  ruolo_cliente: "",
  testo_problema: "",
  testo_ricerca: "",
  testo_risultato: "",
  citazioni_aggiuntive: [],
  meta_fields: [],
  dati_tecnici: [],
  cta_titolo: "Hai una posizione aperta da troppo tempo?",
  logo_iniziale: "",
};

const TIPI = ["Manager", "Sales", "Tech", "Agenti"];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loginInput, setLoginInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | slug
  const [form, setForm] = useState(EMPTY_CASE);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("cases");

  // verify token on load
  useEffect(() => {
    if (!token) return;
    adminVerify(token)
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      });
  }, [token]);

  // load cases when authed
  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    listCaseStudies()
      .then((d) => setCases(d))
      .finally(() => setLoading(false));
  }, [authed]);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!loginInput) return;
    try {
      await adminVerify(loginInput);
      localStorage.setItem(TOKEN_KEY, loginInput);
      setToken(loginInput);
      setAuthed(true);
      toast.success("Accesso effettuato");
    } catch {
      toast.error("Token non valido");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setEditing(null);
  };

  const openNew = () => {
    setForm(EMPTY_CASE);
    setEditing("new");
  };

  const openEdit = (c) => {
    setForm({ ...EMPTY_CASE, ...c });
    setEditing(c.slug);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_CASE);
  };

  const refresh = async () => {
    const d = await listCaseStudies();
    setCases(d);
  };

  const save = async () => {
    if (!form.slug || !form.nome_azienda || !form.ruolo_cercato) {
      toast.error("Slug, nome azienda e ruolo sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // ensure right types for nested arrays
        citazioni_aggiuntive: form.citazioni_aggiuntive.filter((q) => q.testo && q.autore),
        meta_fields: form.meta_fields.filter((m) => m.k && m.v),
        dati_tecnici: form.dati_tecnici.filter((m) => m.k && m.v),
      };
      if (editing === "new") {
        await adminCreateCase(payload, token);
        toast.success("Caso studio creato");
      } else {
        await adminUpdateCase(editing, payload, token);
        toast.success("Caso studio aggiornato");
      }
      await refresh();
      closeForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const del = async (slug) => {
    if (!confirm(`Eliminare il caso "${slug}"?`)) return;
    try {
      await adminDeleteCase(slug, token);
      toast.success("Eliminato");
      await refresh();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // ---------- LOGIN ----------
  if (!authed) {
    return (
      <section className="section bg-lovac min-h-[70vh] flex items-center">
        <Seo title="Admin · Lovac" description="Area riservata Lovac." path="/admin" noindex />
        <div className="container-narrow">
          <div className="card-lovac max-w-md mx-auto">
            <p className="badge-lovac">Admin</p>
            <h1 className="font-display text-3xl mt-4 text-lovac-dark">Accesso CMS</h1>
            <p className="text-sm text-lovac-muted mt-2">Inserisci il token amministratore per gestire i casi studio.</p>
            <form onSubmit={doLogin} className="mt-6 space-y-4">
              <input
                type="password"
                data-testid="admin-token-input"
                className="input-lovac"
                placeholder="Token amministratore"
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
              />
              <button type="submit" data-testid="admin-login-btn" className="btn-primary w-full">
                <LogIn size={16} /> Accedi
              </button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  // ---------- FORM ----------
  if (editing !== null) {
    return (
      <section className="section bg-lovac">
        <div className="container-lovac">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="badge-lovac">Admin CMS</p>
              <h1 className="font-display text-3xl mt-3 text-lovac-dark">
                {editing === "new" ? "Nuovo caso studio" : `Modifica: ${editing}`}
              </h1>
            </div>
            <button onClick={closeForm} className="btn-outline-light"><X size={16} /> Annulla</button>
          </div>

          <div className="card-lovac space-y-6">
            <Field label="Slug (URL)" required>
              <input className="input-lovac" disabled={editing !== "new"} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="es. nuova-azienda" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome azienda" required>
                <input className="input-lovac" value={form.nome_azienda} onChange={(e) => setForm({ ...form, nome_azienda: e.target.value })} />
              </Field>
              <Field label="Logo iniziali (2-3 char)" required>
                <input className="input-lovac" value={form.logo_iniziale} maxLength={3} onChange={(e) => setForm({ ...form, logo_iniziale: e.target.value.toUpperCase() })} />
              </Field>
              <Field label="Settore">
                <input className="input-lovac" value={form.settore} onChange={(e) => setForm({ ...form, settore: e.target.value })} placeholder="es. Energie rinnovabili" />
              </Field>
              <Field label="Tipo figura">
                <select className="input-lovac" value={form.tipo_figura} onChange={(e) => setForm({ ...form, tipo_figura: e.target.value })}>
                  {TIPI.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Ruolo cercato" required>
                <input className="input-lovac" value={form.ruolo_cercato} onChange={(e) => setForm({ ...form, ruolo_cercato: e.target.value })} />
              </Field>
              <Field label="Time to hire">
                <input className="input-lovac" value={form.time_to_hire} onChange={(e) => setForm({ ...form, time_to_hire: e.target.value })} placeholder="es. 30 giorni" />
              </Field>
            </div>

            <Field label="Titolo pagina (dettaglio)">
              <textarea className="input-lovac" rows={2} value={form.titolo_pagina} onChange={(e) => setForm({ ...form, titolo_pagina: e.target.value })} />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Citazione principale">
                <textarea className="input-lovac" rows={3} value={form.citazione_principale} onChange={(e) => setForm({ ...form, citazione_principale: e.target.value })} />
              </Field>
              <div className="space-y-4">
                <Field label="Nome cliente">
                  <input className="input-lovac" value={form.nome_cliente} onChange={(e) => setForm({ ...form, nome_cliente: e.target.value })} />
                </Field>
                <Field label="Ruolo cliente">
                  <input className="input-lovac" value={form.ruolo_cliente} onChange={(e) => setForm({ ...form, ruolo_cliente: e.target.value })} placeholder="es. CEO, Nome Azienda" />
                </Field>
              </div>
            </div>

            <Field label="Il problema (testo)">
              <textarea className="input-lovac" rows={6} value={form.testo_problema} onChange={(e) => setForm({ ...form, testo_problema: e.target.value })} />
            </Field>
            <Field label="La ricerca (testo)">
              <textarea className="input-lovac" rows={6} value={form.testo_ricerca} onChange={(e) => setForm({ ...form, testo_ricerca: e.target.value })} />
            </Field>
            <Field label="Il risultato (testo)">
              <textarea className="input-lovac" rows={3} value={form.testo_risultato} onChange={(e) => setForm({ ...form, testo_risultato: e.target.value })} />
            </Field>

            <ListEditor
              label="Meta fields (intestazione dettaglio)"
              items={form.meta_fields}
              fields={["k", "v"]}
              placeholders={["Etichetta (es. Settore)", "Valore"]}
              onChange={(v) => setForm({ ...form, meta_fields: v })}
            />

            <ListEditor
              label="Dati tecnici (tabella in fondo)"
              items={form.dati_tecnici}
              fields={["k", "v"]}
              placeholders={["Etichetta", "Valore"]}
              onChange={(v) => setForm({ ...form, dati_tecnici: v })}
            />

            <ListEditor
              label="Citazioni aggiuntive"
              items={form.citazioni_aggiuntive}
              fields={["testo", "autore"]}
              placeholders={["Testo citazione", "Autore (es. Nome, Ruolo, Azienda)"]}
              onChange={(v) => setForm({ ...form, citazioni_aggiuntive: v })}
              isTextarea
            />

            <Field label="CTA finale (titolo personalizzato)">
              <input className="input-lovac" value={form.cta_titolo} onChange={(e) => setForm({ ...form, cta_titolo: e.target.value })} />
            </Field>

            <div className="pt-6 border-t border-lovac flex flex-wrap gap-3 justify-end">
              <button onClick={closeForm} className="btn-outline-light">Annulla</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {editing === "new" ? "Crea caso" : "Salva modifiche"}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---------- LIST ----------
  return (
    <section className="section bg-lovac">
      <div className="container-lovac">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="badge-lovac">Admin CMS</p>
            <h1 className="font-display text-4xl mt-3 text-lovac-dark">Gestione contenuti</h1>
          </div>
          <button onClick={logout} className="btn-outline-light"><LogOut size={16} /> Esci</button>
        </div>

        {/* TABS */}
        <div className="flex gap-2 mb-8 border-b border-lovac">
          {[
            { k: "cases", l: "Casi studio", icon: FileText },
            { k: "team", l: "Team", icon: Users },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  tab === t.k ? "border-lovac-accent text-lovac-dark font-medium" : "border-transparent text-lovac-muted hover:text-lovac-dark"
                }`}
              >
                <Icon size={14} /> {t.l}
              </button>
            );
          })}
        </div>

        {tab === "cases" && (
          <>
            <div className="flex justify-end mb-6">
              <button onClick={openNew} className="btn-primary" data-testid="admin-new-btn">
                <Plus size={16} /> Nuovo caso
              </button>
            </div>
            {loading ? (
              <p className="text-lovac-muted">Caricamento…</p>
            ) : (
              <div className="card-lovac p-0 overflow-hidden">
                {cases.map((c, i) => (
                  <div key={c.slug} className={`grid grid-cols-[auto_1fr_auto] items-center gap-4 px-6 py-4 ${i < cases.length - 1 ? "border-b border-lovac" : ""}`}>
                    <div className="h-10 w-10 rounded-lg bg-lovac-2 flex items-center justify-center text-sm font-medium text-lovac-dark">{c.logo_iniziale}</div>
                    <div className="min-w-0">
                      <p className="font-medium text-lovac-dark truncate">{c.nome_azienda}</p>
                      <p className="text-xs text-lovac-muted truncate">{c.tipo_figura} · {c.ruolo_cercato} · {c.time_to_hire} · /{c.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/casi-studio/${c.slug}`} className="btn-outline-light px-3 py-2" title="Vedi"><Eye size={14} /></Link>
                      <button onClick={() => openEdit(c)} className="btn-outline-light px-3 py-2" data-testid={`admin-edit-${c.slug}`}><Pencil size={14} /></button>
                      <button onClick={() => del(c.slug)} className="btn-outline-light px-3 py-2 hover:!border-rose-400 hover:!text-rose-500" data-testid={`admin-delete-${c.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "team" && <TeamManager token={token} />}
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-lovac-muted">
      {label} {required && <span className="text-lovac-accent">*</span>}
    </label>
    <div className="mt-2">{children}</div>
  </div>
);

const ListEditor = ({ label, items, fields, placeholders, onChange, isTextarea = false }) => {
  const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f, ""]))]);
  const update = (i, f, v) => {
    const next = [...items];
    next[i] = { ...next[i], [f]: v };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[11px] uppercase tracking-wider text-lovac-muted">{label}</label>
        <button onClick={add} type="button" className="text-xs text-lovac-accent hover:text-lovac-accent-hover flex items-center gap-1">
          <Plus size={12} /> Aggiungi
        </button>
      </div>
      <div className="mt-2 space-y-2">
        {items.length === 0 && <p className="text-xs text-lovac-muted italic">Vuoto</p>}
        {items.map((item, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2">
            {fields.map((f, fi) =>
              isTextarea && f === "testo" ? (
                <textarea
                  key={f}
                  className="input-lovac col-span-2"
                  rows={2}
                  placeholder={placeholders[fi]}
                  value={item[f] || ""}
                  onChange={(e) => update(i, f, e.target.value)}
                />
              ) : (
                <input
                  key={f}
                  className="input-lovac"
                  placeholder={placeholders[fi]}
                  value={item[f] || ""}
                  onChange={(e) => update(i, f, e.target.value)}
                />
              )
            )}
            <button type="button" onClick={() => remove(i)} className="btn-outline-light px-3 hover:!border-rose-400 hover:!text-rose-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};


/* =========================================
   TEAM MANAGER
   ========================================= */
const EMPTY_MEMBER = { nome: "", ruolo: "", foto_url: "", ordine: 0 };

function TeamManager({ token }) {
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const d = await listTeam();
      setTeam(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm({ ...EMPTY_MEMBER, ordine: team.length }); setEditing("new"); };
  const openEdit = (m) => { setForm({ ...EMPTY_MEMBER, ...m }); setEditing(m.id); };
  const closeForm = () => { setEditing(null); setForm(EMPTY_MEMBER); };

  const save = async () => {
    if (!form.nome || !form.ruolo || !form.foto_url) {
      toast.error("Nome, ruolo e URL foto sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { nome: form.nome, ruolo: form.ruolo, foto_url: form.foto_url, ordine: Number(form.ordine) || 0 };
      if (editing === "new") {
        await adminCreateTeam(payload, token);
        toast.success("Membro creato");
      } else {
        await adminUpdateTeam(editing, payload, token);
        toast.success("Membro aggiornato");
      }
      await refresh();
      closeForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const del = async (m) => {
    if (!confirm(`Eliminare ${m.nome}?`)) return;
    try {
      await adminDeleteTeam(m.id, token);
      toast.success("Eliminato");
      await refresh();
    } catch {
      toast.error("Errore");
    }
  };

  const removeBg = async (m) => {
    if (!confirm(`Rimuovere lo sfondo dalla foto di ${m.nome}?\n\nUsa 1 credito remove.bg.`)) return;
    const t = toast.loading(`Elaborazione foto ${m.nome}…`);
    try {
      await adminTeamRemoveBg(m.id, token);
      toast.success("Sfondo rimosso", { id: t });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rimozione sfondo", { id: t });
    }
  };

  if (editing !== null) {
    return (
      <div className="card-lovac space-y-4 max-w-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-lovac-dark">
            {editing === "new" ? "Nuovo membro" : `Modifica: ${form.nome}`}
          </h2>
          <button onClick={closeForm} className="btn-outline-light"><X size={14} /> Annulla</button>
        </div>
        <Field label="Nome completo" required>
          <input className="input-lovac" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        </Field>
        <Field label="Ruolo" required>
          <input className="input-lovac" value={form.ruolo} placeholder="es. Recruiter senior · Sales" onChange={(e) => setForm({ ...form, ruolo: e.target.value })} />
        </Field>
        <Field label="URL foto (preferibilmente sfondo trasparente PNG)" required>
          <input className="input-lovac" value={form.foto_url} placeholder="https://..." onChange={(e) => setForm({ ...form, foto_url: e.target.value })} />
          {form.foto_url && (
            <div className="mt-3 w-28 aspect-square rounded-xl overflow-hidden" style={{ background: "linear-gradient(160deg, var(--lovac-accent), var(--lovac-accent-hover))" }}>
              <img src={form.foto_url} alt="preview" className="w-full h-full object-cover object-top" />
            </div>
          )}
        </Field>
        <Field label="Ordine (numerico, crescente)">
          <input type="number" className="input-lovac" value={form.ordine} onChange={(e) => setForm({ ...form, ordine: e.target.value })} />
        </Field>
        <div className="pt-4 border-t border-lovac flex gap-3 justify-end">
          <button onClick={closeForm} className="btn-outline-light">Annulla</button>
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salva
          </button>
        </div>
      </div>
    );
  }

  const removeBgAll = async () => {
    const toProcess = team.filter((m) => m.foto_url && !m.foto_url.startsWith("/api/team-photo/"));
    if (toProcess.length === 0) {
      toast.info("Tutte le foto hanno già lo sfondo rimosso");
      return;
    }
    if (!confirm(`Rimuovere lo sfondo da ${toProcess.length} foto?\n\nUsa ~${toProcess.length} crediti remove.bg (piano free: 50/mese).`)) return;
    const t = toast.loading(`Elaborazione ${toProcess.length} foto in background…`);
    try {
      const kick = await adminTeamRemoveBgAll(token);
      if (kick.already_ok) {
        toast.success("Tutto già a posto", { id: t });
        return;
      }
      // Poll status every 2s
      const total = kick.queued;
      let done = false;
      while (!done) {
        await new Promise((res) => setTimeout(res, 2000));
        const s = await adminTeamRemoveBgStatus(token);
        toast.loading(`Elaborazione ${s.processed + s.failed}/${total} …`, { id: t });
        if (s.status === "done") {
          const parts = [];
          if (s.processed) parts.push(`${s.processed} ok`);
          if (s.failed) parts.push(`${s.failed} falliti`);
          toast.success(`Fatto: ${parts.join(" · ")}`, { id: t });
          done = true;
        }
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rimozione sfondo massiva", { id: t });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <p className="text-sm text-lovac-muted">{team.length} membri pubblicati su /chi-siamo</p>
        <div className="flex gap-2">
          <button onClick={removeBgAll} className="btn-outline-light" title="Rimuovi lo sfondo da tutte le foto team">
            <Sparkles size={14} /> Rimuovi sfondo a tutte
          </button>
          <button onClick={openNew} className="btn-primary"><Plus size={16} /> Nuovo membro</button>
        </div>
      </div>
      {loading ? (
        <p className="text-lovac-muted">Caricamento…</p>
      ) : team.length === 0 ? (
        <div className="card-lovac text-center py-12 text-lovac-muted">Nessun membro pubblicato. Aggiungi il primo.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {team.map((m) => (
            <div key={m.id} className="card-lovac flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl overflow-hidden shrink-0" style={{ background: "linear-gradient(160deg, var(--lovac-accent), var(--lovac-accent-hover))" }}>
                <img src={m.foto_url} alt={m.nome} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-lovac-dark truncate">{m.nome}</p>
                <p className="text-xs text-lovac-muted truncate">{m.ruolo} · ord {m.ordine}</p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => removeBg(m)} className="btn-outline-light px-2.5 py-1.5" title="Rimuovi sfondo (remove.bg)"><Sparkles size={13} /></button>
                <button onClick={() => openEdit(m)} className="btn-outline-light px-2.5 py-1.5"><Pencil size={13} /></button>
                <button onClick={() => del(m)} className="btn-outline-light px-2.5 py-1.5 hover:!border-rose-400 hover:!text-rose-500"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
